import React, { useState, useEffect } from 'react';
import {
  Box, Card, CardContent, TextField, Button,
  Typography, InputAdornment, IconButton,
  Divider, Alert, CircularProgress, Chip
} from '@mui/material';
import {
  PhoneAndroid, Lock, Visibility,
  VisibilityOff, QrCode2, WifiOff, Wifi
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { login } from '../api/authApi';
import { useAuth } from '../context/AuthContext';
import { useApp } from '../context/AppContext';
import { tryOfflineLogin, saveAuthCache, refreshAllCaches } from '../utils/offlineDB';
import api from '../api/authApi';

export default function Login() {
  const { theme } = useApp();
  const navigate         = useNavigate();
  const { loginSuccess } = useAuth();

  const [mobile,   setMobile]   = useState('');
  const [password, setPassword] = useState('');
  const [showPwd,  setShowPwd]  = useState(false);
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState('');
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  useEffect(() => {
    const on  = () => setIsOnline(true);
    const off = () => setIsOnline(false);
    window.addEventListener('online',  on);
    window.addEventListener('offline', off);
    return () => { window.removeEventListener('online', on); window.removeEventListener('offline', off); };
  }, []);

  const handleLogin = async () => {
    setError('');
    if (!mobile || mobile.length !== 10) { setError('Please enter a valid 10-digit mobile number.'); return; }
    if (!password) { setError('Please enter your password.'); return; }
    setLoading(true);

    if (isOnline) {
      try {
        const res = await login({ mobileNumber: mobile, password });
        if (res.data.success) {
          const d = res.data.data;
          try { await saveAuthCache(d.user || d, d.accessToken, d.refreshToken, password); } catch {}
          loginSuccess(d);
          setTimeout(async () => { try { await refreshAllCaches(api); } catch {} }, 1500);
          navigate('/dashboard');
        } else {
          setError(res.data.message || 'Login failed.');
        }
      } catch (err) {
        // An HTTP response means the API is reachable (for example, a 401 for
        // invalid credentials). Do not revive an old cached token in that case.
        if (err.response) {
          setError(err.response.data?.message || 'Login failed. Please check your credentials.');
          setLoading(false);
          return;
        }

        const off = await tryOfflineLogin(mobile, password);
        if (off.success) {
          loginSuccess({ user: off.user, accessToken: off.accessToken, refreshToken: off.refreshToken });
          navigate('/dashboard');
        } else {
          setError('Cannot connect to server. Please check your connection.');
        }
      }
    } else {
      const off = await tryOfflineLogin(mobile, password);
      if (off.success) {
        loginSuccess({ user: off.user, accessToken: off.accessToken, refreshToken: off.refreshToken });
        navigate('/dashboard');
      } else {
        setError(off.reason || 'Offline login failed. Please connect to internet first.');
      }
    }
    setLoading(false);
  };

  return (
    <Box sx={{ minHeight:'100vh', background:'linear-gradient(135deg, #f0f7f4 0%, #e8f5e9 100%)', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', p:2 }}>

      <Box sx={{ textAlign:'center', mb:3 }}>
        <Typography variant="h4" sx={{ fontWeight:800, color:theme.primary, fontFamily:'Poppins, sans-serif' }}>🐄 MicroDairy</Typography>
        <Box sx={{ display:'flex', alignItems:'center', justifyContent:'center', gap:1, mt:0.5 }}>
          <Typography variant="h6" sx={{ color:'#555' }}>Login</Typography>
          <Chip size="small"
            icon={isOnline ? <Wifi sx={{ fontSize:'14px !important' }}/> : <WifiOff sx={{ fontSize:'14px !important' }}/>}
            label={isOnline ? 'Online' : 'Offline'}
            sx={{ fontSize:'0.7rem', fontWeight:700, background:isOnline?'#dcfce7':'#fff7ed', color:isOnline?'#15803d':'#f97316', border:`1px solid ${isOnline?'#bbf7d0':'#fed7aa'}`, '& .MuiChip-icon':{ color:'inherit !important' } }}
          />
        </Box>
      </Box>

      <Card sx={{ width:'100%', maxWidth:900, borderRadius:3, boxShadow:'0 4px 24px rgba(26,122,74,0.10)', overflow:'hidden' }}>
        <CardContent sx={{ p:0 }}>
          <Box sx={{ display:'grid', gridTemplateColumns:{ xs:'1fr', md:'1fr auto 1fr' } }}>

            <Box sx={{ p:{ xs:3, md:5 } }}>
              <Typography variant="subtitle1" sx={{ fontWeight:700, color:'#888', mb:3, textTransform:'uppercase', letterSpacing:1 }}>
                Sign in to your account
              </Typography>

              {!isOnline && (
                <Alert severity="warning" icon={<WifiOff sx={{ fontSize:18 }}/>} sx={{ mb:2, borderRadius:2, fontSize:'0.82rem' }}>
                  <strong>No internet.</strong> You can still login if you logged in before on this device.
                </Alert>
              )}

              {error && <Alert severity="error" sx={{ mb:2, borderRadius:2 }}>{error}</Alert>}

              <TextField fullWidth label="Mobile Number" value={mobile}
                onChange={(e) => setMobile(e.target.value.replace(/\D/g,'').slice(0,10))}
                onKeyPress={(e) => e.key==='Enter' && handleLogin()}
                inputProps={{ maxLength:10, inputMode:'numeric' }}
                InputProps={{ startAdornment:<InputAdornment position="start"><PhoneAndroid sx={{ color:theme.primary }}/></InputAdornment> }}
                sx={{ mb:2 }}/>

              <TextField fullWidth label="Password" type={showPwd?'text':'password'} value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyPress={(e) => e.key==='Enter' && handleLogin()}
                InputProps={{
                  startAdornment:<InputAdornment position="start"><Lock sx={{ color:theme.primary }}/></InputAdornment>,
                  endAdornment:<InputAdornment position="end"><IconButton onClick={() => setShowPwd(!showPwd)}>{showPwd?<VisibilityOff/>:<Visibility/>}</IconButton></InputAdornment>
                }} sx={{ mb:1 }}/>

              {isOnline && (
                <Typography variant="body2" onClick={() => navigate('/forgot-password')}
                  sx={{ color:theme.primary, fontWeight:700, cursor:'pointer', mb:3, '&:hover':{ textDecoration:'underline' } }}>
                  Forgot password?
                </Typography>
              )}

              <Button fullWidth variant="contained" size="large" onClick={handleLogin} disabled={loading}
                sx={{
                  background:!isOnline?'linear-gradient(135deg,#f97316,#ea6c00)':`linear-gradient(135deg,${theme.primary},${theme.hover})`,
                  borderRadius:2, py:1.5, fontWeight:700, fontSize:'1rem', fontFamily:'Poppins, sans-serif',
                  mt:isOnline?0:2,
                  '&:hover':{ background:!isOnline?'#c45100':`linear-gradient(135deg,${theme.dark},${theme.primary})` }
                }}>
                {loading ? <CircularProgress size={24} color="inherit"/> : !isOnline ? '🔒 Login Offline' : 'Login'}
              </Button>

              {!isOnline && (
                <Typography variant="caption" sx={{ display:'block', mt:1.5, color:'#999', textAlign:'center', lineHeight:1.5 }}>
                  Uses your last login data stored on this device.
                </Typography>
              )}
            </Box>

            <Box sx={{ display:{ xs:'none', md:'flex' }, alignItems:'center', justifyContent:'center', px:1 }}>
              <Divider orientation="vertical" flexItem/>
              <Typography variant="caption" sx={{ position:'absolute', background:'#fff', px:1, color:'#999', fontWeight:700 }}>OR</Typography>
            </Box>

            <Box sx={{ p:{ xs:3, md:5 }, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', background:'#fafffe' }}>
              <Typography variant="subtitle2" sx={{ fontWeight:700, color:'#888', mb:2, textTransform:'uppercase', letterSpacing:1 }}>
                Scan QR to Login
              </Typography>
              <Box sx={{ width:160, height:160, border:'2px solid #e0e0e0', borderRadius:3, display:'flex', alignItems:'center', justifyContent:'center', background:'#f9f9f9', cursor:'pointer', '&:hover':{ borderColor:theme.primary } }}>
                <QrCode2 sx={{ fontSize:120, color:theme.primary, opacity:0.7 }}/>
              </Box>
              <Typography variant="caption" sx={{ color:'#aaa', mt:1 }}>Click to refresh QR</Typography>
            </Box>

          </Box>
        </CardContent>

        <Box sx={{ p:2, background:'#f8fafc', borderTop:'1px solid #eee', display:'flex', alignItems:'center', justifyContent:'center', gap:2 }}>
          <Typography variant="body2" sx={{ color:'#888', fontWeight:600 }}>Not Registered Yet?</Typography>
          <Button variant="contained" onClick={() => navigate('/register')}
            sx={{ background:'linear-gradient(135deg, #f97316, #fb923c)', borderRadius:20, px:3, fontWeight:700, boxShadow:'0 4px 16px rgba(249,115,22,0.3)', '&:hover':{ background:'linear-gradient(135deg, #ea6c0a, #f97316)' } }}>
            Register Now
          </Button>
        </Box>
      </Card>
    </Box>
  );
}
